import { useState, useEffect, useContext, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Typography, Alert, Box, Link } from "@mui/material";

import Modal from "../../components/Modal/Modal";
import Form from "../../components/Form/Form";
import Input from "../../components/Form/Input/Input";

import { BrandContext } from "../../App";
import { useTheme } from "@mui/material/styles";

import Logo from "../../assets/logos/2x-logo.png";
import LoginBG from "../../assets/laptop-mockup.jpg";

import { LoginContainer } from "./styles";

import LinearProgress from "@mui/material/LinearProgress";

import DefaultButton from "../../components/simple/DefaultButton";

import { useAPI } from "../../hooks/useAPI";

import { useAuth } from "../../contexts/AuthContext";
import { useUI } from "../../contexts/UIContext";

const LoginPage = () => {
  const isManualLogin = useRef(false);
  const rememberMe = useRef(false);
  const queryClient = useQueryClient();
  const theme = useTheme();
  // const [showPassword, setShowPassword] = useState(false);
  // const [showPasswordResetLink, setShowPasswordResetLink] = useState(false);
  // const [loginCredentials, setLoginCredentials] = useState({});
  const [loginErrorMessage, setLoginErrorMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState("");
  const [userId, setUserId] = useState("");
  const [successfulLoginRequestData, setSuccessfulLoginRequestData] =
    useState(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileNumberModalOpen, setMobileNumberModalOpen] = useState(false);
  const [partialAuthToken, setPartialAuthToken] = useState("");
  const { brand, featureFlags } = useContext(BrandContext);

  const { setLayoutMode } = useUI();

  const { authToken, saveAuthToken, loggedInUser, fetchUser, isLoading } =
    useAuth();

  const { callAPI } = useAPI();

  const navigate = useNavigate();
  const [qParams] = useSearchParams();

  const handleLogin = async (data) => {
    var responseKeys = Object.keys(data);

    if (responseKeys.includes("2fa_already_exists_on_account")) {
      localStorage.setItem("2fa_already_exists_on_account", "1");
    }

    if (responseKeys.includes("token")) {
      isManualLogin.current = true;

      let currentFlags = featureFlags;

      if (!currentFlags || Object.keys(currentFlags).length === 0) {
        try {
          currentFlags = await queryClient.fetchQuery({
            queryKey: ["feature_flags"],
            queryFn: () => callAPI(`feature_flags`, { token: data["token"] }),
          });
          queryClient.setQueryData(["feature_flags"], currentFlags);
        } catch (error) {
          console.error("Failed to fetch feature flags during login", error);
          currentFlags = {};
        }
      }

      let dest: string;
      if (
        data["user"]?.subscriptions?.on_demand_coaches?.length > 0 &&
        currentFlags?.on_demand_coach
      ) {
        setLayoutMode("minimal");
        dest = "/dashboard/on_demand_coach";
      } else {
        setLayoutMode("full");
        if (qParams.has("next") && qParams.get("next") !== "/login") {
          dest = qParams.get("next")!;
        } else if (qParams.has("programInterest")) {
          dest = `/dashboard/skills-academy/${qParams.get("programInterest")}`;
        } else {
          dest = "/dashboard/home";
        }
      }

      sessionStorage.setItem("post_login_redirect", dest);
      saveAuthToken(data["token"], rememberMe.current);
    } else if (responseKeys.includes("partial_auth_token")) {
      // requires 2FA
      setPartialAuthToken(data["partial_auth_token"]);
      // check if 2FA setup is required
      if (
        responseKeys.includes("requires_2fa_setup") &&
        data["requires_2fa_setup"] == true
      ) {
        const dest = data["code_sent_to"] || data["mobile_number_masked"];
        if (dest || mobileNumber != "") {
          if (dest) {
            localStorage.setItem("2fa_code_sent_to", dest);
          }
          if (data["delivery_method"]) {
            localStorage.setItem("2fa_delivery_method", data["delivery_method"]);
          }
          if (data["email_backup_available"]) {
            localStorage.setItem("2fa_email_backup_available", "1");
          } else {
            localStorage.removeItem("2fa_email_backup_available");
          }
          var mobNumber = mobileNumber || dest;
          navigate(`/2fa_init/${data["partial_auth_token"]}`, {
            state: { mobile_number: mobNumber },
          });
        } else {
          // User has not provided mobile number yet, start that flow
          setPartialAuthToken(data["partial_auth_token"]);
          setMobileNumberModalOpen(true);
        }
      } else if (
        responseKeys.includes("requires_2fa") &&
        data["requires_2fa"] == true
      ) {
        // redirect to 2FA verification
        const dest = data["code_sent_to"] || data["mobile_number_masked"];
        if (dest) {
          localStorage.setItem("2fa_code_sent_to", dest);
        }
        if (data["delivery_method"]) {
          localStorage.setItem("2fa_delivery_method", data["delivery_method"]);
        }
        if (data["email_backup_available"]) {
          localStorage.setItem("2fa_email_backup_available", "1");
        } else {
          localStorage.removeItem("2fa_email_backup_available");
        }
        navigate(`/2fa_verify/${data["partial_auth_token"]}`);
      }
    } else {
      setLoginErrorMessage(
        "There was a problem signing you in, please try again",
      );
      // setShowPasswordResetLink(true);
    }
    setLoading(false);
  };

  const handleError = (err) => {
    setLoginErrorMessage(err);
    setLoading(false);
  };

  useEffect(() => {
    if (!authToken || authToken.length === 0) return;
    const stored = sessionStorage.getItem("post_login_redirect");
    if (stored) {
      sessionStorage.removeItem("post_login_redirect");
      navigate(stored, { replace: true });
    } else if (qParams.has("programInterest")) {
      navigate(`/dashboard/skills-academy/${qParams.get("programInterest")}`, { replace: true });
    } else if (!isManualLogin.current) {
      navigate("/dashboard", { replace: true });
    }
  }, [authToken, navigate]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.primary,
        height: "100vh",
        paddingTop: "20px",
        paddingBottom: "20px",
        display: "flex",
        alignItems: "center",
        alignSelf: "stretch",
      }}
    >
      <LoginContainer>
        <div className="form-container">
          <div className="login-form">
            <img src={Logo} alt="Guru Now" />
            <Typography variant="h1">Welcome back</Typography>
            {qParams.has("signup") && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                Account created, please login below
              </Alert>
            )}
            {qParams.has("after") && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                Your password was changed successfully. Please log in with your
                new password
              </Alert>
            )}

            {!qParams.has("signup") && !qParams.has("after") && (
              <p>Please enter your login details</p>
            )}

            {qParams.has("session") && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                Your two-factor authentication session has expired and you have
                now been logged out
              </Alert>
            )}

            {qParams.get("reason") === "inactivity" && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                You were signed out due to inactivity
              </Alert>
            )}

            <Form
              action={`login`}
              onBefore={() => setLoading(true)}
              onValues={(vals) => { rememberMe.current = !!(vals as Record<string, unknown>).remember; }}
              onSuccess={handleLogin}
              onError={handleError}
            >
              {({ register, formState: { errors }, watch }) => (
                <>
                  <Input
                    id="login-email"
                    showLabel
                    label="Email"
                    type="email"
                    register={register("username", {
                      required: "Email address is required",
                    })}
                    error={errors.username?.message}
                  />
                  <Input
                    id="login-password"
                    showLabel
                    label="Password"
                    type="password"
                    register={register("password", {
                      required: "Password is required",
                    })}
                    error={errors.password?.message}
                  />
                  <div className="login-utils">
                    <div className="remember-me">
                      <Input
                        id="login-remember"
                        showLabel
                        label="Remember for 30 days"
                        type="checkbox"
                        register={register("remember")}
                      />
                    </div>
                    <Link className="forgot-link" href="/reset_password">
                      Forgot password?
                    </Link>
                  </div>
                  {loginErrorMessage && (
                    <>
                      <Alert severity="error" sx={{ mb: 4 }}>
                        {loginErrorMessage}
                      </Alert>
                    </>
                  )}
                  <DefaultButton type="submit">Login</DefaultButton>
                  {loading && (
                    <LinearProgress
                      id="login-loading"
                      color="secondary"
                      sx={{
                        mt: 5,
                        height: "8px",
                        borderRadius: "6px",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#962EFF",
                          borderRadius: "6px",
                        },
                      }}
                    />
                  )}
                </>
              )}
            </Form>
            <div className="register-helper">
              Don't have an account?{" "}
              <Link className="sign-up-link" href="">
                Sign up
              </Link>
            </div>
          </div>
        </div>
        <div className="full-background">
          <img src={LoginBG} alt="" />
        </div>
      </LoginContainer>
      <Modal
        id="twofa-prompt"
        title="Secure your account with two-factor authentication"
        open={mobileNumberModalOpen}
        onClose={() => setMobileNumberModalOpen(false)}
        showIcon
        showClose={false}
        slim
        icon={
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 10V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V10M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z"
              stroke="#01ECFF"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        }
      >
        <p style={{ margin: "0 0 15px 0", fontSize: "14px", fontWeight: 400 }}>
          We've added two-factor authentication to improve account security.
          Please add a mobile number to continue.
        </p>

        <Form
          action={`2fa/enable`}
          method="POST"
          onSuccess={(data) => {
            // Update user details
            const dest = data["code_sent_to"] || data["mobile_number_masked"];
            if (dest) {
              localStorage.setItem("2fa_code_sent_to", dest);
            }
            if (data["delivery_method"]) {
              localStorage.setItem("2fa_delivery_method", data["delivery_method"]);
            }
            navigate(`/2fa_init/${partialAuthToken}`);
          }}
          onError={(err) => {
            setTwoFAError(err);
          }}
        >
          {({ register, formState: { errors }, watch, control }) => {
            const watchedMobile = watch("mobile_number");
            useEffect(() => {
              if (watchedMobile) {
                setMobileNumber(watchedMobile);
              }
            }, [watchedMobile]);
            return (
              <>
                <input
                  type="hidden"
                  name="partial_auth_token"
                  value={partialAuthToken}
                  {...register("partial_auth_token")}
                />
                <Input
                  type="mobile"
                  label="Mobile Number"
                  id="mobile_number"
                  error={errors?.mobile_number?.message}
                  register={register("mobile_number", {
                    required: "Mobile number is required",
                  })}
                  control={control}
                />
                <Input
                  id="twofa_consent"
                  type="checkbox"
                  label="I agree to GuruNow sending me a verification code every time I log in. Message and data rates may apply."
                  register={register("twofa_consent", {
                    required: "Consent is required",
                  })}
                  error={errors?.twofa_consent?.message}
                />
                <DefaultButton type="submit" className="action-button">
                  Verify
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.1665 10H15.8332M15.8332 10L9.99984 4.16669M15.8332 10L9.99984 15.8334"
                      stroke="#030039"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </DefaultButton>
              </>
            );
          }}
        </Form>
        {twoFAError && (
          <Alert severity="error" sx={{ mt: 4 }}>
            {twoFAError}
          </Alert>
        )}
      </Modal>
    </Box>
  );
};

export default LoginPage;
